import { Component } from '@theme/component';

class ProductRecommendations extends Component {
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /**
   * Observing changes to the elements attributes
   * @type {MutationObserver}
   */
  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;

      if (mutation.attributeName === 'data-error') continue;

      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;

      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;

      if (mutation.attributeName === 'data-product-id' && this.dataset.context === 'cart') {
        this.#resetRecommendationsState();
      }

      this.#loadRecommendations();
      break;
    }
  });

  /**
   * The cached recommendations
   * @type {Record<string, string>}
   */
  #cachedRecommendations = {};

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /** @type {(() => void) | null} */
  #cartDrawerOpenHandler = null;

  connectedCallback() {
    super.connectedCallback();

    if (this.dataset.context === 'cart') {
      this.#resolveCartSectionId();
      this.#bindCartDrawerOpen();
      this.#loadRecommendations();
    } else {
      this.#intersectionObserver = new IntersectionObserver(
        (entries, observer) => {
          if (!entries[0]?.isIntersecting) return;

          observer.disconnect();
          this.#loadRecommendations();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      this.#intersectionObserver.observe(this);
    }

    this.#mutationObserver.observe(this, { attributes: true });

    if (this.dataset.recommendationsPerformed === 'true') {
      this.#syncVisibility();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#mutationObserver.disconnect();
    this.#unbindCartDrawerOpen();
  }

  /**
   * Resolves section id for cart mounts from the global API host or theme setting.
   */
  #resolveCartSectionId() {
    if (this.dataset.sectionId) return;

    const layout = this.dataset.layout || 'carousel';
    const host = document.querySelector(
      `[data-cart-recommendations-host] product-recommendations[data-layout="${layout}"]`
    );
    const hostSectionId = host?.dataset.sectionId;

    if (hostSectionId) {
      this.dataset.sectionId = hostSectionId;
    }
  }

  /**
   * Clears performed state so a new product id can refetch recommendations.
   */
  #resetRecommendationsState() {
    delete this.dataset.recommendationsPerformed;
    this.classList.remove('hidden');
    delete this.dataset.error;
  }

  /**
   * Load recommendations when the cart drawer opens (drawer content may not intersect viewport).
   */
  #bindCartDrawerOpen() {
    if (this.dataset.context !== 'cart') return;

    this.#cartDrawerOpenHandler = () => {
      this.#loadRecommendations();
    };

    document.querySelector('cart-drawer-component')?.addEventListener('dialog:open', this.#cartDrawerOpenHandler);
  }

  #unbindCartDrawerOpen() {
    if (!this.#cartDrawerOpenHandler) return;

    document.querySelector('cart-drawer-component')?.removeEventListener('dialog:open', this.#cartDrawerOpenHandler);
    this.#cartDrawerOpenHandler = null;
  }

  /**
   * Show or hide based on SSR markup (`data-has-recommendations`).
   */
  #syncVisibility() {
    const hasRecommendations = Boolean(this.querySelector('[data-has-recommendations="true"]'));

    if (hasRecommendations) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }
  }

  /**
   * Load the product recommendations
   */
  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      if (this.dataset.context === 'cart') {
        this.classList.add('hidden');
      }
      return;
    }

    if (!sectionId) {
      this.#resolveCartSectionId();
    }

    if (!this.dataset.sectionId) {
      return;
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, this.dataset.sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        let recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (!recommendations?.innerHTML && this.dataset.context === 'cart') {
          recommendations = html.querySelector('product-recommendations');
        }

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;

          this.#syncVisibility();
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  /**
   * Fetches the recommendations and cached the result for future use
   * @param {string} productId
   * @param {string | undefined} sectionId
   * @param {string | undefined} intent
   * @returns {Promise<{ success: true, data: string } | { success: false, status: number }>}
   */
  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Handle errors in a consistent way
   * @param {Error} error
   */
  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}
